import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

Sentry.init({
  dsn: "https://e379943700c30161441d9c4b75f718e1@o4507669269970944.ingest.us.sentry.io/4508044355108864",

  // We specify the optional "transport" property to track errors offline.
  // https://docs.sentry.io/platforms/javascript/guides/react/best-practices/offline-caching/
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

  /**
   * Consider some additional options in the future:
   * Create source maps for the overhead-keyboard-notes-worker (other sourcemaps are already created) - https://docs.sentry.io/platforms/javascript/guides/react/sourcemaps/
   * Separate production/other environments - https://docs.sentry.io/platforms/javascript/guides/react/configuration/environments/
   * React-specific features - https://docs.sentry.io/platforms/javascript/guides/react/features/
   * Enrich events with user/app version information - https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/
   * Get user feedback when a crash occurs - https://docs.sentry.io/platforms/javascript/guides/react/user-feedback/#crash-report-modal
   */

  /**
   * Consider these integrations in the future:
   * @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/browserprofiling
   * @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/captureconsole (but limited to only in errors)
   * @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/extraerrordata (doubtful usefulness)
   * @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/sessiontiming/
   */
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    /** @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/replay/ */
    Sentry.replayIntegration(),
    /** @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/reportingobserver/ */
    Sentry.reportingObserverIntegration(),
    /** @link https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/httpclient */
    Sentry.httpClientIntegration(),
  ],

  tracesSampleRate: 0.0001, // 0.01% - Be conservative. We don't want to go over 10M spans/mo.

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    // This is a default setting.
    /^\//,

    // Ensure the server-side CORS will allow the trace header to be passed before uncommenting this block.
    // https://docs.sentry.io/platforms/javascript/guides/react/tracing/trace-propagation/dealing-with-cors-issues/
    // // We probably don't need any of these. Test before removing.
    // /^https:\/\/app\.playgroundsessions\.com/,
    // /^https:\/\/local-app\.playgroundsessions\.com/,
    // /^https:\/\/dev-app\.playgroundsessions\.com/,
    // /^localhost:3000/,
    //
    // // We'll want this if we ever add Sentry to our server.
    // /^https:\/\/api\.playgroundsessions\.com/,
    // /^https:\/\/local-api\.playgroundsessions\.com/,
    // /^https:\/\/dev-api\.playgroundsessions\.com/,
  ],

  replaysSessionSampleRate: 0.0, // We don't want to be charged for this.
  replaysOnErrorSampleRate: 50/5000, // We get 5k errors and 50 replays for free under the "Developer" plan.

  // https://docs.sentry.io/platforms/javascript/guides/react/features/redux/#normalization-depth
  normalizeDepth: 10, // Or however deep you want your state context to be.
});
